import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import type { Role } from '@/types/user';

import { useTranslation } from '../../../hooks/useTranslation';

export type NavItem = {
  id: string;
  label: string;
  sectionId?: string;
  href?: string;
};

export const useHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const { isAuthenticated, hasAnyRole } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const allowedRoles: Role[] = ['admin', 'agent'];
  const hasRequiredRole = allowedRoles.length !== 0 || hasAnyRole(allowedRoles);
  const isAuthorized = isAuthenticated && hasRequiredRole;

  const navItems: NavItem[] = [
    { id: 'home', label: t('home.header.home'), sectionId: 'hero' },
    { id: 'services', label: t('home.header.services'), sectionId: 'services' },
    { id: 'about', label: t('home.header.about'), sectionId: 'benefits' },
    {
      id: 'contact',
      label: t('home.header.contact'),
      sectionId: 'testimonials',
    },
  ];

  if (isAuthorized) {
    navItems.push({ id: 'dashboard', label: 'Dashboard', href: '/dashboard' });
  }

  const data = {
    navItems,
    ctaText: t('home.header.getQuote'),
    isMenuOpen,
  };

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);

    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }

    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (item: NavItem) => {
    if (item.href) {
      navigate({ to: item.href });
      setIsMenuOpen(false);
    } else if (item.sectionId) {
      scrollToSection(item.sectionId);
    }
  };

  const handleCtaClick = () => {
    navigate({ to: '/form' });
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return {
    data,
    handleNavClick,
    handleCtaClick,
    toggleMenu,
  };
};

export type HeaderProps = ReturnType<typeof useHeader>;
